/**
 * Deployment-time inputs for the client half of the session lifecycle.
 *
 * `VITE_SELOGATE_API_URL` is the P2/P3/P4/P5 HTTP API's base URL and `VITE_SELOGATE_STAGE`
 * names the deployment. **The stage is part of every storage key** (`device-cache.md`:
 * *"Partition by authenticated user and deployment stage"*), so a developer pointing the
 * same origin at `dev` and then at `staging` never restores the wrong environment's session.
 */

const env = import.meta.env as unknown as Readonly<Record<string, string | undefined>>

/** Trailing slashes stripped so `${API_BASE_URL}/auth/otp/start` is always well-formed. */
export const API_BASE_URL: string = (env['VITE_SELOGATE_API_URL'] ?? '').replace(/\/+$/, '')

export const STAGE: string = env['VITE_SELOGATE_STAGE'] ?? 'dev'

/**
 * The header P2's middleware reads the session binding from.
 *
 * It is a **wire constant**, restated here rather than imported: the server's copy lives in
 * `packages/functions/auth/config.ts`, which is a Lambda module the browser bundle must not
 * pull in, and `@selogate/core` — the one package both sides may share — does not export it.
 * Promoting it into `contracts.md` would be the tidy fix and needs a decision; logged as an
 * assumption in `implementation-notes.md` → P6 rather than taken unilaterally.
 */
export const SESSION_BINDING_HEADER = 'x-selogate-session'

/**
 * The header this client states its build in — **diagnostic only** (rung 2 of
 * `review/05-client-proof-ladder.md`).
 *
 * A **wire constant**, restated here for exactly the reason `SESSION_BINDING_HEADER` above is:
 * the server's copy is in `packages/functions/auth/config.ts`, a Lambda module the browser
 * bundle must not pull in.
 *
 * **It is one of two halves and neither works alone.** `infra/auth.ts`'s `cors.allowHeaders`
 * must list it, because a browser refuses to send a header its preflight did not allow — so a
 * client that sends this against an API that does not permit it loses **every authenticated
 * call**, not just the diagnostic. `scripts/smoke-test.mjs`'s `cors-preflight` asserts the
 * deployed API allows it, which is what makes that failure loud instead of a browser-only
 * mystery.
 */
export const CLIENT_BUILD_HEADER = 'x-selogate-build'

/**
 * 🔸 Refresh this many seconds before the access token actually expires, so a request that
 * starts valid does not arrive expired. Cheap: the refresh is one call and the token is
 * short-lived either way.
 */
export const ACCESS_TOKEN_SKEW_SECONDS = 60

/** 🔸 Resend window on the OTP screen. §7.11 fixes the send *cap*, not this countdown. */
export const OTP_RESEND_SECONDS = 30

/**
 * 🔸 D42's self-name length, matching P2's `MAX_SELF_NAME_GRAPHEMES`.
 *
 * Shared by the two surfaces that write a self-name — onboarding capture (`AuthProvider`) and
 * the drawer's editor (D133) — because a second copy of a limit is a limit that drifts.
 */
export const MAX_NAME_GRAPHEMES = 60
